import React, { useCallback, useEffect, useState } from 'react';
import { authedFetch, isAdmin } from './utils/api';
import { color, naira, radius, space } from './ui/theme';
import { Banner, Btn, Card, Chip } from './ui/kit';

/**
 * VAT: the rate, how each product is treated, and the input VAT you can claim.
 *
 * The distinction this screen exists for is zero-rated versus exempt. Both put
 * nothing on a customer's invoice; only one lets the company reclaim the VAT
 * it paid on raw materials and packaging. For a wound-care manufacturer that
 * is a monthly sum, and the system previously had no way to record which
 * applied.
 *
 * Nothing is classified by default, and the rate is not used until somebody
 * signs for it -- the same gate payroll rates have, for the same reason.
 */

const TREATMENTS = [
  ['ZERO_RATED', 'Zero-rated',
   '0% to the customer, and input VAT on related costs IS recoverable.'],
  ['STANDARD', 'Standard rate', 'Charged at the standard rate.'],
  ['EXEMPT', 'Exempt',
   '0% to the customer, and input VAT is NOT recoverable.'],
  ['OUT_OF_SCOPE', 'Out of scope', 'Outside the scope of VAT entirely.'],
];

const VERDICT = {
  RECLAIMABLE: ['success', 'A credit can be claimed back'],
  NOT_RECLAIMABLE: ['danger', 'A credit cannot be claimed back'],
  PARTIAL: ['warning', 'Input VAT has to be apportioned'],
  UNCERTAIN: ['warning', 'Cannot be stated yet'],
  NO_SALES: ['neutral', 'No sales in this period'],
};

const inputStyle = {
  width: '100%', padding: '8px 10px', borderRadius: radius.sm,
  border: `1px solid ${color.border}`, fontSize: 13,
};

async function req(url, opts) {
  const res = await authedFetch(url, opts);
  if (!res.ok) {
    let d = `Request failed (${res.status})`;
    try { const j = await res.json(); d = j.detail || j.message || d; } catch { /* keep */ }
    throw new Error(typeof d === 'string' ? d : 'Request failed');
  }
  return res.json();
}

const monthStart = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
};
const today = () => new Date().toISOString().slice(0, 10);

export default function VatDesk({ notify }) {
  const [cfg, setCfg] = useState(null);
  const [products, setProducts] = useState(null);
  const [position, setPosition] = useState(null);
  const [purchases, setPurchases] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [signer, setSigner] = useState('');
  const [source, setSource] = useState('');
  const [basis, setBasis] = useState(
    'Nigeria Tax Act 2025 — medical and pharmaceutical products');
  const [entry, setEntry] = useState({
    invoice_number: '', vendor_name: '', invoice_date: today(),
    total_amount: '', tax_amount: '',
  });
  const admin = isAdmin();

  const load = useCallback(async () => {
    setError(null);
    try {
      const [c, p, pos, pur] = await Promise.all([
        req('/api/vat/config'),
        req('/api/vat/products'),
        req(`/api/vat/claim-position?start=${monthStart()}&end=${today()}`),
        req(`/api/vat/purchases?start=${monthStart()}&end=${today()}`),
      ]);
      setCfg(c); setProducts(p); setPosition(pos); setPurchases(pur);
    } catch (e) { setError(e.message); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const act = async (url, opts, ok) => {
    setBusy(true);
    try {
      const r = await req(url, opts);
      notify?.(r.message || r.note || ok, 'success');
      await load();
      return r;
    } catch (e) { notify?.(e.message, 'error'); return null; }
    finally { setBusy(false); }
  };

  const classify = (productId, treatment) => {
    if (!treatment) return;
    if (basis.trim().length < 5) {
      notify?.('Say what the treatment rests on first', 'error'); return;
    }
    act(`/api/vat/products/${productId}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ treatment, basis: basis.trim() }),
    }, 'Treatment recorded.');
  };

  if (error) return <Banner tone="danger" title="Could not load VAT">{error}</Banner>;
  if (!cfg) return null;

  const [tone, label] = VERDICT[position?.verdict] || ['neutral', ''];

  return (
    <div>
      {/* ---- the rate --------------------------------------------------- */}
      <Card style={{ marginBottom: space(2) }}>
        <div style={{ display: 'flex', justifyContent: 'space-between',
                      alignItems: 'center', marginBottom: space(1) }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 15 }}>VAT rate</h3>
            <div style={{ fontSize: 12.5, color: color.textSecondary }}>
              {cfg.name} &middot; from {cfg.effective_from}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 22, fontWeight: 700 }}>
              {cfg.standard_rate}%
            </div>
            {cfg.is_confirmed
              ? <Chip tone="success">Confirmed</Chip>
              : <Chip tone="danger">Not confirmed</Chip>}
          </div>
        </div>

        {!cfg.is_confirmed && (
          <Banner tone="danger" title="No VAT is charged from this rate yet">
            {cfg.warning}
            {' '}Zero-rated products are unaffected &mdash; nothing times
            anything is still nothing &mdash; but nothing can be charged at the
            standard rate until this is signed for.
          </Banner>
        )}

        {cfg.notes && (
          <div style={{ fontSize: 12.5, color: color.textSecondary,
                        marginTop: space(1) }}>
            {cfg.notes}
          </div>
        )}

        {admin && !cfg.is_confirmed && (
          <div style={{ marginTop: space(1.5) }}>
            <div style={{ display: 'grid', gap: 8, marginBottom: 8,
                          gridTemplateColumns:
                            'repeat(auto-fit, minmax(190px, 1fr))' }}>
              <input style={inputStyle} value={signer}
                     placeholder="Name of the person signing off"
                     onChange={(e) => setSigner(e.target.value)} />
              <input style={inputStyle} value={source}
                     placeholder="Checked against (e.g. Nigeria Tax Act 2025)"
                     onChange={(e) => setSource(e.target.value)} />
            </div>
            <Btn size="sm" disabled={busy}
                 onClick={() => {
                   if (signer.trim().length < 3) {
                     notify?.('Enter who is taking responsibility', 'error');
                     return;
                   }
                   act(`/api/vat/config/${cfg.id}/confirm`, {
                     method: 'POST',
                     headers: { 'Content-Type': 'application/json' },
                     body: JSON.stringify({
                       confirmed_by: signer.trim(),
                       source_reference: source.trim() || null }),
                   }, 'Rate confirmed.');
                 }}>
              Confirm this rate
            </Btn>
          </div>
        )}
      </Card>

      {/* ---- can a credit be claimed ------------------------------------ */}
      {position && (
        <Card style={{ marginBottom: space(2) }}>
          <div style={{ display: 'flex', justifyContent: 'space-between',
                        alignItems: 'center', marginBottom: 6 }}>
            <h3 style={{ margin: 0, fontSize: 15 }}>This month&rsquo;s position</h3>
            <Chip tone={tone}>{label}</Chip>
          </div>
          <div style={{ fontSize: 13 }}>{position.detail}</div>
          <div style={{ fontSize: 12.5, color: color.textSecondary,
                        marginTop: space(1) }}>
            Turnover {naira(position.turnover_total)} &middot; input VAT
            recorded {naira(purchases?.input_vat_total || 0)}
          </div>
        </Card>
      )}

      {/* ---- products ---------------------------------------------------- */}
      <Card style={{ marginBottom: space(2) }}>
        <h3 style={{ margin: 0, fontSize: 15 }}>How each product is treated</h3>
        <div style={{ fontSize: 12.5, color: color.textSecondary,
                      margin: '4px 0 10px' }}>
          {products?.note}
        </div>

        {admin && (
          <div style={{ marginBottom: space(1.5) }}>
            <div style={{ fontSize: 11.5, color: color.textSecondary,
                          marginBottom: 4 }}>
              What the treatment rests on &mdash; recorded against every
              classification you set below
            </div>
            <input style={inputStyle} value={basis}
                   onChange={(e) => setBasis(e.target.value)} />
          </div>
        )}

        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ textAlign: 'left', fontSize: 12,
                         color: color.textSecondary }}>
              <th style={{ padding: '6px 4px' }}>Product</th>
              <th style={{ padding: '6px 4px' }}>Treatment</th>
              <th style={{ padding: '6px 4px' }}>Basis</th>
            </tr>
          </thead>
          <tbody>
            {(products?.items || []).map((p) => (
              <tr key={p.product_id} style={{ borderTop: `1px solid ${color.border}` }}>
                <td style={{ padding: '6px 4px' }}>
                  {p.name}
                  <span style={{ color: color.textMuted, fontSize: 11.5 }}>
                    {' '}{p.sku}
                  </span>
                </td>
                <td style={{ padding: '6px 4px' }}>
                  {admin ? (
                    <select style={{ ...inputStyle, width: 'auto' }}
                            value={p.treatment || ''} disabled={busy}
                            onChange={(e) => classify(p.product_id, e.target.value)}>
                      <option value="">Not classified</option>
                      {TREATMENTS.map(([v, l]) => (
                        <option key={v} value={v}>{l}</option>
                      ))}
                    </select>
                  ) : (
                    p.treatment
                      ? <Chip tone={p.treatment === 'EXEMPT' ? 'warning' : 'success'}>
                          {p.treatment.replace(/_/g, ' ')}
                        </Chip>
                      : <span style={{ color: color.textMuted }}>not classified</span>
                  )}
                  {p.means && (
                    <div style={{ fontSize: 11, color: color.textMuted,
                                  marginTop: 2, maxWidth: 260 }}>
                      {p.means}
                    </div>
                  )}
                </td>
                <td style={{ padding: '6px 4px', fontSize: 11.5,
                             color: color.textSecondary }}>
                  {p.basis || '—'}
                  {p.classified_by && (
                    <div style={{ color: color.textMuted }}>
                      by {p.classified_by}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {/* ---- input VAT --------------------------------------------------- */}
      <Card>
        <h3 style={{ margin: 0, fontSize: 15 }}>Input VAT on purchases</h3>
        <div style={{ fontSize: 12.5, color: color.textSecondary,
                      margin: '4px 0 10px' }}>
          VAT you paid your suppliers. On zero-rated sales this is reclaimable,
          so every supplier invoice entered here is money you can ask for back.
          An invoice number and date are required &mdash; a claim without them
          is the first thing an audit removes.
        </div>

        {admin && (
          <div style={{ display: 'grid', gap: 8, marginBottom: space(1.5),
                        gridTemplateColumns:
                          'repeat(auto-fit, minmax(150px, 1fr))' }}>
            <input style={inputStyle} placeholder="Invoice number"
                   value={entry.invoice_number}
                   onChange={(e) => setEntry({ ...entry, invoice_number: e.target.value })} />
            <input style={inputStyle} placeholder="Supplier"
                   value={entry.vendor_name}
                   onChange={(e) => setEntry({ ...entry, vendor_name: e.target.value })} />
            <input style={inputStyle} type="date" value={entry.invoice_date}
                   onChange={(e) => setEntry({ ...entry, invoice_date: e.target.value })} />
            <input style={inputStyle} placeholder="Invoice total" inputMode="decimal"
                   value={entry.total_amount}
                   onChange={(e) => setEntry({ ...entry, total_amount: e.target.value })} />
            <input style={inputStyle} placeholder="VAT on it" inputMode="decimal"
                   value={entry.tax_amount}
                   onChange={(e) => setEntry({ ...entry, tax_amount: e.target.value })} />
            <Btn size="sm" disabled={busy}
                 onClick={async () => {
                   const total = Number(entry.total_amount);
                   const tax = Number(entry.tax_amount);
                   if (!entry.invoice_number.trim() || !entry.vendor_name.trim()) {
                     notify?.('Invoice number and supplier are both needed', 'error');
                     return;
                   }
                   if (!(total > 0) || !(tax >= 0)) {
                     notify?.('Enter the invoice total and the VAT on it', 'error');
                     return;
                   }
                   const r = await act('/api/vat/purchases', {
                     method: 'POST',
                     headers: { 'Content-Type': 'application/json' },
                     body: JSON.stringify({
                       invoice_number: entry.invoice_number.trim(),
                       vendor_name: entry.vendor_name.trim(),
                       invoice_date: entry.invoice_date,
                       total_amount: total, tax_amount: tax }),
                   }, 'Input VAT recorded.');
                   if (r) setEntry({ ...entry, invoice_number: '',
                                     total_amount: '', tax_amount: '' });
                 }}>
              Record
            </Btn>
          </div>
        )}

        <div style={{ fontSize: 13, marginBottom: 8 }}>
          Recorded this month: <strong>{naira(purchases?.input_vat_total || 0)}</strong>
        </div>

        {(purchases?.items || []).length === 0 ? (
          <div style={{ fontSize: 12.5, color: color.textMuted }}>
            No supplier invoices with VAT recorded for this month.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: 'left', fontSize: 12,
                           color: color.textSecondary }}>
                <th style={{ padding: '6px 4px' }}>Invoice</th>
                <th style={{ padding: '6px 4px' }}>Supplier</th>
                <th style={{ padding: '6px 4px' }}>Date</th>
                <th style={{ padding: '6px 4px', textAlign: 'right' }}>Total</th>
                <th style={{ padding: '6px 4px', textAlign: 'right' }}>VAT</th>
              </tr>
            </thead>
            <tbody>
              {purchases.items.map((p) => (
                <tr key={p.id} style={{ borderTop: `1px solid ${color.border}` }}>
                  <td style={{ padding: '6px 4px' }}>{p.invoice_number}</td>
                  <td style={{ padding: '6px 4px' }}>{p.vendor_name}</td>
                  <td style={{ padding: '6px 4px' }}>{p.invoice_date}</td>
                  <td style={{ padding: '6px 4px', textAlign: 'right' }}>
                    {naira(p.total_amount)}
                  </td>
                  <td style={{ padding: '6px 4px', textAlign: 'right',
                               fontWeight: 600 }}>
                    {naira(p.tax_amount)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}
